// src/pages/AdminTraumato.jsx
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { fetchTraumatoCatalog, saveTraumatoCatalog } from '../api/traumato';
import Modal from '../components/Modal';
import PageLayout from '../components/PageLayout';
import { getToken } from '../utils/apiFetch';

function ActRow({ act, onChange, onDelete }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_130px_130px_92px] gap-2 sm:gap-3 items-start">
      <input
        className="w-full rounded border px-3 py-2"
        placeholder="Code"
        value={act.code || ''}
        onChange={(e) => onChange({ ...act, code: e.target.value })}
      />
      <textarea
        className="w-full rounded border px-3 py-2 min-h-[40px]"
        placeholder="Libellé"
        value={act.label || ''}
        onChange={(e) => onChange({ ...act, label: e.target.value })}
      />
      <input
        className="w-full rounded border px-3 py-2"
        type="number"
        step="0.01"
        placeholder="CPAM"
        value={act.cpam ?? ''}
        onChange={(e) =>
          onChange({ ...act, cpam: e.target.value === '' ? null : Number(e.target.value) })
        }
      />
      <input
        className="w-full rounded border px-3 py-2"
        type="number"
        step="0.01"
        placeholder="Dépassement %"
        value={act.depassementRate ?? ''}
        onChange={(e) =>
          onChange({
            ...act,
            depassementRate: e.target.value === '' ? null : Number(e.target.value),
          })
        }
      />
      <div className="flex sm:justify-end">
        <button
          onClick={onDelete}
          className="w-full sm:w-auto px-3 py-2 rounded border border-rose-300 text-rose-700 hover:bg-rose-50"
        >
          Supprimer
        </button>
      </div>
    </div>
  );
}

export default function AdminTraumato() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({ categories: [] });
  const [dirty, setDirty] = useState(false);

  // UI: état accordéons
  const [openIds, setOpenIds] = useState(new Set());

  // Overlay: ajouter catégorie
  const [catOpen, setCatOpen] = useState(false);
  const [catName, setCatName] = useState('');

  // Overlay: ajouter acte
  const [actOpen, setActOpen] = useState(false);
  const [actCatId, setActCatId] = useState(null);
  const [newAct, setNewAct] = useState({ code: '', label: '', cpam: null, depassementRate: 0 });

  // Charger seulement quand le token est présent (évite le flash 401 + redirect)
  const token = getToken?.();
  useEffect(() => {
    if (!token) return;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchTraumatoCatalog();
        setCatalog(data || { categories: [] });
        setDirty(false);
      } catch (e) {
        console.error(e);
        toast.error('Chargement impossible');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const totalDirty = useMemo(() => (dirty ? 1 : 0), [dirty]);

  function toggle(id) {
    setOpenIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function addCategoryInline(name) {
    const id = crypto.randomUUID();
    const next = {
      categories: [
        ...catalog.categories,
        {
          id,
          name: name || 'Nouvelle catégorie',
          order: (catalog.categories?.length || 0) + 1,
          acts: [],
        },
      ],
    };
    setCatalog(next);
    setDirty(true);
    setOpenIds((s) => new Set([...s, id]));
  }

  function addActInline(catId, act) {
    const next = {
      categories: catalog.categories.map((c) =>
        c.id === catId
          ? { ...c, acts: [...(c.acts || []), { ...act, id: crypto.randomUUID() }] }
          : c,
      ),
    };
    setCatalog(next);
    setDirty(true);
  }

  async function handleSave() {
    try {
      const payload = {
        categories: catalog.categories.map((c, i) => ({
          id: c.id,
          name: c.name,
          order: i + 1,
          acts: (c.acts || []).map((a, j) => ({
            id: a.id,
            code: (a.code || '').trim(),
            label: (a.label || '').trim(),
            cpam: a.cpam == null || a.cpam === '' ? null : Number(a.cpam),
            depassementRate:
              a.depassementRate == null || a.depassementRate === ''
                ? null
                : Number(a.depassementRate),
            order: j + 1,
          })),
        })),
      };
      await saveTraumatoCatalog(payload);
      toast.success('Catalogue enregistré');
      setDirty(false);
    } catch (e) {
      console.error(e);
      toast.error('Échec de l’enregistrement');
    }
  }

  function handleDeleteCategory(catId) {
    setCatalog((prev) => ({
      categories: prev.categories.filter((c) => c.id !== catId),
    }));
    setDirty(true);
  }

  function handleChangeCategoryName(catId, name) {
    setCatalog((prev) => ({
      categories: prev.categories.map((c) => (c.id === catId ? { ...c, name } : c)),
    }));
    setDirty(true);
  }

  function handleChangeAct(catId, idx, nextAct) {
    setCatalog((prev) => ({
      categories: prev.categories.map((c) =>
        c.id !== catId ? c : { ...c, acts: c.acts.map((a, i) => (i === idx ? nextAct : a)) },
      ),
    }));
    setDirty(true);
  }

  function handleDeleteAct(catId, idx) {
    setCatalog((prev) => ({
      categories: prev.categories.map((c) =>
        c.id !== catId ? c : { ...c, acts: c.acts.filter((_, i) => i !== idx) },
      ),
    }));
    setDirty(true);
  }

  return (
    <PageLayout title="Admin — Catalogue Traumatologie" padBottom>
      <div className="px-3 sm:px-5 py-4 max-w-5xl mx-auto">
        {/* sticky banner — on laisse un petit offset pour ne pas coller sous le bandeau PREPROD */}
        <div className="sticky top-2 z-[90] mb-4">
          {dirty && (
            <div className="rounded-xl border bg-amber-50/70 backdrop-blur px-3 sm:px-4 py-3 flex items-center justify-between shadow">
              <div className="text-amber-800 text-sm">
                {totalDirty} modification(s) non sauvegardée(s)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-2 rounded border border-slate-300 bg-white hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Sauver ({totalDirty})
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 mb-4">
          <h1 className="text-2xl sm:text-3xl font-semibold">Admin — Catalogue Traumatologie</h1>
          <button
            onClick={() => setCatOpen(true)}
            className="px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-black"
          >
            + Catégorie
          </button>
        </div>

        {loading ? (
          <div className="text-slate-500">Chargement…</div>
        ) : (
          <div className="space-y-4">
            {catalog.categories.map((cat) => {
              const isOpen = openIds.has(cat.id);
              const actsCount = cat.acts?.length || 0;
              return (
                <div key={cat.id} className="rounded-2xl border bg-white shadow-sm">
                  {/* En-tête (accordéon) */}
                  <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-3">
                    <button
                      onClick={() => toggle(cat.id)}
                      className="flex-1 text-left flex items-center gap-3"
                      title="Ouvrir / fermer"
                    >
                      <span className="inline-block w-5 text-slate-500">{isOpen ? '▾' : '▸'}</span>
                      <input
                        value={cat.name}
                        onChange={(e) => handleChangeCategoryName(cat.id, e.target.value)}
                        className="min-w-0 w-full sm:w-[520px] font-medium text-lg sm:text-xl bg-transparent focus:bg-white border rounded px-2 py-1"
                      />
                    </button>

                    <span className="shrink-0 inline-flex items-center justify-center h-10 px-3 rounded-full bg-slate-100 text-slate-700 text-sm">
                      {actsCount} {actsCount > 1 ? 'actes' : 'acte'}
                    </span>

                    <div className="shrink-0 flex gap-2">
                      <button
                        onClick={() => {
                          setActCatId(cat.id);
                          setNewAct({ code: '', label: '', cpam: null, depassementRate: 0 });
                          setActOpen(true);
                          setOpenIds((s) => new Set([...s, cat.id]));
                        }}
                        className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
                        title="Ajouter un acte"
                      >
                        + Acte
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="px-3 py-2 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50"
                        title="Supprimer la catégorie"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>

                  {/* Corps */}
                  {isOpen && (
                    <div className="px-3 sm:px-4 pb-4">
                      {/* entêtes responsive */}
                      <div className="hidden sm:grid sm:grid-cols-[140px_1fr_130px_130px_92px] gap-3 text-sm text-slate-600 mb-2">
                        <div>Code</div>
                        <div>Libellé</div>
                        <div>CPAM</div>
                        <div>Dépassement %</div>
                        <div className="text-right">Actions</div>
                      </div>

                      <div className="space-y-3">
                        {(cat.acts || []).map((a, idx) => (
                          <ActRow
                            key={a.id || idx}
                            act={a}
                            onChange={(na) => handleChangeAct(cat.id, idx, na)}
                            onDelete={() => handleDeleteAct(cat.id, idx)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Overlay: catégorie */}
      <Modal
        open={catOpen}
        title="Nouvelle catégorie"
        onClose={() => setCatOpen(false)}
        actions={
          <>
            <button
              onClick={() => setCatOpen(false)}
              className="px-3 py-2 rounded border border-slate-300 bg-white hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              onClick={() => {
                const name = (catName || '').trim();
                if (!name) return toast.error('Nom requis');
                addCategoryInline(name);
                setCatName('');
                setCatOpen(false);
              }}
              className="px-3 py-2 rounded bg-slate-900 text-white hover:bg-black"
            >
              Ajouter
            </button>
          </>
        }
      >
        <label className="block text-sm mb-1">Nom</label>
        <input
          autoFocus
          className="w-full rounded border px-3 py-2"
          placeholder="Libellé de la catégorie"
          value={catName}
          onChange={(e) => setCatName(e.target.value)}
        />
      </Modal>

      {/* Overlay: acte */}
      <Modal
        open={actOpen}
        title="Nouvel acte"
        onClose={() => setActOpen(false)}
        actions={
          <>
            <button
              onClick={() => setActOpen(false)}
              className="px-3 py-2 rounded border border-slate-300 bg-white hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              onClick={() => {
                const a = {
                  code: (newAct.code || '').trim(),
                  label: (newAct.label || '').trim(),
                  cpam: newAct.cpam == null || newAct.cpam === '' ? null : Number(newAct.cpam),
                  depassementRate:
                    newAct.depassementRate == null || newAct.depassementRate === ''
                      ? null
                      : Number(newAct.depassementRate),
                };
                if (!a.code || !a.label) return toast.error('Code et libellé requis');
                addActInline(actCatId, a);
                setActOpen(false);
              }}
              className="px-3 py-2 rounded bg-slate-900 text-white hover:bg-black"
            >
              Ajouter
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm mb-1">Code</label>
            <input
              autoFocus
              className="w-full rounded border px-3 py-2"
              value={newAct.code}
              onChange={(e) => setNewAct((x) => ({ ...x, code: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">CPAM</label>
            <input
              className="w-full rounded border px-3 py-2"
              type="number"
              step="0.01"
              value={newAct.cpam ?? ''}
              onChange={(e) =>
                setNewAct((x) => ({
                  ...x,
                  cpam: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm mb-1">Libellé</label>
            <textarea
              className="w-full rounded border px-3 py-2 min-h-[64px]"
              value={newAct.label}
              onChange={(e) => setNewAct((x) => ({ ...x, label: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Dépassement %</label>
            <input
              className="w-full rounded border px-3 py-2"
              type="number"
              step="0.01"
              value={newAct.depassementRate ?? ''}
              onChange={(e) =>
                setNewAct((x) => ({
                  ...x,
                  depassementRate: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
            />
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}
